import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    IMyPf, IPfMember, IPfMemberCreateRequest, IPfMemberLedger, IPfMemberUpdateRequest, IPfProfitPreview,
    IPfProfitRequest, IPfProfitYear, IPfRun, IPfRunDetail, IPfRunPreview, IPfRunRequest, IPfSettings,
    IPfSettingsRequest,
} from '../interfaces/providentFund.interface';

const headers = () => ({ headers: AuthService.getAuthHeader() });

/** A failed blob download carries its JSON error as a Blob; surface the message so the toast can show it. */
async function rethrowBlobError(error: any): Promise<never> {
    const data = error?.response?.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            const text = await data.text();
            let message = text;
            try {
                const parsed = JSON.parse(text);
                if (parsed && typeof parsed.message === 'string') message = parsed.message;
            } catch {
                // not JSON: keep the raw text
            }
            if (message) error.response.data = message;
        } catch {
            // keep the original error
        }
    }
    throw error;
}

const filenameFrom = (disposition: string | undefined, fallback: string): string => {
    const match = disposition ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition) : null;
    return match ? decodeURIComponent(match[1]) : fallback;
};

async function downloadPdf(url: string, fallbackName: string): Promise<void> {
    try {
        const response = await axios.get(url, { ...headers(), responseType: 'blob' });
        const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', filenameFrom(response.headers?.['content-disposition'], fallbackName));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
        return rethrowBlobError(error);
    }
}

// W3-J: provident fund, HR side and employee self-service.
export const ProvidentFundService = {
    getSettings: async (institutionId: string): Promise<IPfSettings> =>
        (await axios.get(ENDPOINTS.PROVIDENT_FUND.SETTINGS(institutionId), headers())).data,

    updateSettings: async (institutionId: string, req: IPfSettingsRequest): Promise<IPfSettings> =>
        (await axios.put(ENDPOINTS.PROVIDENT_FUND.SETTINGS(institutionId), req, headers())).data,

    // ---- members ----
    listMembers: async (institutionId: string): Promise<IPfMember[]> =>
        (await axios.get(ENDPOINTS.PROVIDENT_FUND.MEMBERS(institutionId), headers())).data,

    createMember: async (institutionId: string, req: IPfMemberCreateRequest): Promise<IPfMember> =>
        (await axios.post(ENDPOINTS.PROVIDENT_FUND.MEMBERS(institutionId), req, headers())).data,

    updateMember: async (memberId: string, req: IPfMemberUpdateRequest): Promise<IPfMember> =>
        (await axios.put(ENDPOINTS.PROVIDENT_FUND.MEMBER(memberId), req, headers())).data,

    ledger: async (memberId: string): Promise<IPfMemberLedger> =>
        (await axios.get(ENDPOINTS.PROVIDENT_FUND.MEMBER_LEDGER(memberId), headers())).data,

    downloadStatement: (memberId: string): Promise<void> =>
        downloadPdf(ENDPOINTS.PROVIDENT_FUND.MEMBER_STATEMENT(memberId), 'pf-statement.pdf'),

    // ---- contribution runs ----
    listRuns: async (institutionId: string): Promise<IPfRun[]> =>
        (await axios.get(ENDPOINTS.PROVIDENT_FUND.RUNS(institutionId), headers())).data,

    previewRun: async (institutionId: string, year: number, month: number): Promise<IPfRunPreview> =>
        (await axios.get(ENDPOINTS.PROVIDENT_FUND.RUN_PREVIEW(institutionId), { ...headers(), params: { year, month } })).data,

    postRun: async (institutionId: string, req: IPfRunRequest): Promise<IPfRun> =>
        (await axios.post(ENDPOINTS.PROVIDENT_FUND.RUNS(institutionId), req, headers())).data,

    getRun: async (runId: string): Promise<IPfRunDetail> =>
        (await axios.get(ENDPOINTS.PROVIDENT_FUND.RUN(runId), headers())).data,

    reverseRun: async (runId: string): Promise<IPfRun> =>
        (await axios.post(ENDPOINTS.PROVIDENT_FUND.RUN_REVERSE(runId), null, headers())).data,

    // ---- annual profit allocation ----
    profitYears: async (institutionId: string): Promise<IPfProfitYear[]> =>
        (await axios.get(ENDPOINTS.PROVIDENT_FUND.PROFIT(institutionId), headers())).data,

    profitPreview: async (institutionId: string, year: number, distributableProfit: number): Promise<IPfProfitPreview> =>
        (await axios.get(ENDPOINTS.PROVIDENT_FUND.PROFIT_PREVIEW(institutionId), {
            ...headers(), params: { year, distributableProfit },
        })).data,

    profitApply: async (institutionId: string, req: IPfProfitRequest): Promise<IPfProfitPreview> =>
        (await axios.post(ENDPOINTS.PROVIDENT_FUND.PROFIT(institutionId), req, headers())).data,

    profitReverse: async (institutionId: string, year: number): Promise<void> => {
        await axios.post(ENDPOINTS.PROVIDENT_FUND.PROFIT_REVERSE(institutionId, year), null, headers());
    },

    // ---- employee self-service ("My Provident Fund") ----
    mine: async (institutionId: string): Promise<IMyPf> =>
        (await axios.get(ENDPOINTS.MY_PROVIDENT_FUND.MINE(institutionId), headers())).data,

    downloadMyStatement: (institutionId: string): Promise<void> =>
        downloadPdf(ENDPOINTS.MY_PROVIDENT_FUND.STATEMENT(institutionId), 'pf-statement.pdf'),
};
